/**
 * PublicLayout — shared wrapper for all /p/* public pages.
 * Renders PublicHeader (guest or signed-in state, from the stored session)
 * above the routed page.
 *
 * The previous Arcee-branded navbar (app logo / VelLogo, Home pill, member-link
 * pill) is kept below, commented out, with the lines it needed.
 */

// import { pageHref } from '../../utils/pageHref';
// import { Outlet, Link, useMatch } from 'react-router-dom';
import { Outlet, useMatch, useLocation } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
// import { useEffect, useRef, useState } from 'react';
import { useEffect } from 'react';
// import envConfig from '../../config/envConfig';
// import VelLogo from '../VelLogo';
// import { usePublicApp } from '../../context/PublicAppContext';
import PublicBreadcrumbs from './PublicBreadcrumbs';
// import '../../styles/Header.css';
import PublicHeader from './PublicHeader';
// The athlete's phone tab bar, borrowed for the motion board so /results is
// not the one place a signed-in athlete loses it. Its rules are all fd-
// prefixed and its colours come from FEED_TOKENS on the wrapper below, so
// nothing reaches the rest of the public pages.
import { FeedBottomNav } from '../feed/FeedSidebar';
import FEED_TOKENS from '../../theme/feedTokens';
import { useFeedUser, useAthleteSummary } from '../../pages/feed/useFeedData';
import { MOTION_BOARD_PATH } from '../../pages/feed/feedRoutes';
import '../../styles/feed.css';

/**
 * The bottom tab bar on the results pages, for a signed-in athlete only — its
 * tabs are their own feed, which a visitor without an account cannot open.
 * The bar itself is phone-only by CSS, exactly as in FeedLayout.
 */
function ResultsBottomNav() {
  const user = useFeedUser();
  // Same cache entry FeedLayout uses — the Passes badge costs no extra request.
  const { data: summary } = useAthleteSummary(user.userId);
  if (user.userId === null || user.userId === undefined) return null;
  return (
    // .fd-shell carries the tokens and fonts; the min-height and ground it
    // also sets are cancelled, since the bar and its sheet are fixed and this
    // wrapper should take no room at all.
    <div className="fd-shell" style={{ ...FEED_TOKENS, minHeight: 0, background: 'transparent' }}>
      <FeedBottomNav registrationCount={summary?.registrations?.total || 0} />
    </div>
  );
}

export default function PublicLayout() {
  const { theme, setTheme, themesList } = useTheme();
  // const { appBranding } = usePublicApp();
  // A table's list page sits on plain white so its cards' shadows carry the
  // depth; the other public pages keep the tinted canvas.
  const onList = useMatch('/p/:tableSlug');
  // The motion board (/results and one race's board) matches the white list
  // pages too — its cards and dark hero read against plain ground.
  const onResultsIndex = useMatch('/results');
  const onBoard = useMatch('/results/:eventActivityId');
  const whiteCanvas = onList || onResultsIndex || onBoard;
  const accentColor = theme.primary;
  // const configuredLogo = appBranding?.logo || null;
  // const configuredAppName = appBranding?.app_name || envConfig.appName;
  const { pathname } = useLocation();
  // Every motion-board page: the index, a race's board and the run behind a result.
  const onResults = pathname === MOTION_BOARD_PATH || pathname.startsWith(`${MOTION_BOARD_PATH}/`);

  // Public pages load their content asynchronously, so the browser's own scroll
  // restoration lands at a stale offset once the record renders. Take over
  // scrolling while these routes are mounted and always open at the top.
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  // A client-side navigation keeps the previous page's scroll offset (e.g. a
  // card clicked halfway down the list), so reset it on every route change.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // // A page this application wants reachable from every public page — "My
  // // registrations", "My orders", "My tickets". Configured per application, so
  // // an application that has not set one renders NOTHING extra: no disabled
  // // control, no placeholder, no changed markup. Both halves are required, so a
  // // half-filled config cannot produce a link to nowhere.
  // const memberLink = appBranding?.member_link;
  // const memberLinkReady = Boolean(memberLink?.page && memberLink?.label);

  // When rendered inside the admin preview iframe, receive the admin's active
  // theme via postMessage and apply its CSS variables to this document.
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type !== 'ARCEE_THEME') return;
      const t = event.data.theme;
      if (!t) return;
      const match = themesList?.find(th => th.name === t.name);
      if (match) {
        setTheme(match);
      } else {
        const root = document.documentElement;
        if (t.primary) root.style.setProperty('--primary-color', t.primary);
        if (t.secondary) root.style.setProperty('--secondary-color', t.secondary);
        if (t.cardRadius) root.style.setProperty('--card-radius', t.cardRadius);
        if (t.buttonRadius) root.style.setProperty('--button-radius', t.buttonRadius);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setTheme, themesList]);

  // // Shared chrome for the navbar's right-hand actions (home + member link),
  // // so the icon button and the labelled link stay visually identical.
  // const pillStyle = {
  //   color: accentColor,
  //   textDecoration: 'none',
  //   fontWeight: 600,
  //   padding: '6px 14px',
  //   borderRadius: 'var(--button-radius)',
  //   background: `${accentColor}0e`,
  //   border: `1px solid ${accentColor}28`,
  //   fontSize: '0.82rem',
  //   transition: 'background 0.2s ease, border-color 0.2s ease',
  // };
  // const applyPillHover = e => {
  //   e.currentTarget.style.background = `${accentColor}18`;
  //   e.currentTarget.style.borderColor = `${accentColor}50`;
  // };
  // const applyPillRest = e => {
  //   e.currentTarget.style.background = `${accentColor}0e`;
  //   e.currentTarget.style.borderColor = `${accentColor}28`;
  // };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: whiteCanvas ? '#fff' : '#f8fafc' }}>

      {/* ── Ambient background gradient ── */}
      {!whiteCanvas && <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          height: 320,
          background: `radial-gradient(ellipse 80% 100% at 20% -20%, ${accentColor}14 0%, transparent 70%),
                       radial-gradient(ellipse 60% 80% at 80% -10%, ${accentColor}0b 0%, transparent 60%)`,
          pointerEvents: 'none',
          zIndex: 0,
          transition: 'opacity 0.5s ease',
        }}
      />}

      {/* Previous navbar — commented out at the bottom of this file. */}
      <PublicHeader />

      {/* ── Page content ── */}
      <main className={onResults ? 'fd-bnav-clear' : undefined} style={{ flex: 1, position: 'relative', zIndex: 1 }}>
        {/* Breadcrumbs are hidden on list pages (/p/:tableSlug) only — the
            header's active tab already says where you are there. Detail pages
            (/p/:tableSlug/:recordId) keep them.
            Previously rendered on every page: <PublicBreadcrumbs /> */}
        {!onList && <PublicBreadcrumbs />}
        <Outlet />
      </main>

      {onResults && <ResultsBottomNav />}

    </div>
  );
}

/* ── Previous navbar (replaced by PublicHeader) ───────────────────────────────
   Restore by uncommenting it back into PublicLayout's return in place of
   <PublicHeader />, together with the commented imports, the appBranding /
   configuredLogo / memberLink lines and the pillStyle helpers above. */
//       {/* ── Glassmorphic Sticky Navbar ── */}
//       <nav
//         className="navbar navbar-expand-lg py-2 bg-white"
//         style={{
//           background: 'white',
//           borderBottom: '1px solid rgba(0,0,0,0.06)',
//           boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
//           position: 'sticky',
//           top: 0,
//           zIndex: 1000,
//         }}
//       >
//         <div className="container-fluid px-4 d-flex align-items-center justify-content-between">
//
//           {/* Logo + app name — uses configured logo when available.
//               Links to the site root. */}
//           <Link
//             to="/"
//             className="navbar-brand d-flex align-items-center gap-2"
//             style={{ textDecoration: 'none' }}
//           >
//             {configuredLogo ? (
//               <img
//                 src={configuredLogo}
//                 alt={`${configuredAppName} logo`}
//                 style={{
//                   height: 40,
//                   width: 'auto',
//                   maxWidth: 160,
//                   objectFit: 'contain',
//                   borderRadius: 6,
//                 }}
//                 onError={(e) => { e.currentTarget.style.display = 'none'; }}
//               />
//             ) : (
//               <VelLogo size={30} />
//             )}
//             <span
//               className="arcee-logo-text"
//               style={{ fontSize: '1.3rem', letterSpacing: '-0.02em' }}
//             >
//               {configuredAppName}
//             </span>
//           </Link>
//
//           {/* Right side: home icon, then the member link when configured. The
//               home button shows on every public page — the brand mark also links
//               to "/", but that reads as branding, not navigation. */}
//           <div className="d-flex align-items-center gap-2">
//             <Link
//               to="/"
//               aria-label="Home"
//               title="Home"
//               className="d-flex align-items-center justify-content-center"
//               style={{
//                 ...pillStyle,
//                 width: 34,
//                 height: 34,
//                 padding: 0,
//               }}
//               onMouseEnter={applyPillHover}
//               onMouseLeave={applyPillRest}
//             >
//               <i className="bi bi-house-door" style={{ fontSize: '0.95rem' }} />
//             </Link>
//
//             {/* Configured per application; absent everywhere else, and then
//                 this renders nothing at all. The destination is a Page Builder
//                 page slug, so the application decides where it goes as well as
//                 what it is called — nothing here names a registration. */}
//             {memberLinkReady && (
//               <Link
//                 to={pageHref(memberLink.page)}
//                 aria-label={memberLink.label}
//                 title={memberLink.label}
//                 className="d-flex align-items-center gap-1 small"
//                 style={pillStyle}
//                 onMouseEnter={applyPillHover}
//                 onMouseLeave={applyPillRest}
//               >
//                 <i
//                   className={`bi ${memberLink.icon || 'bi-person-check'}`}
//                   style={{ fontSize: '0.75rem' }}
//                 />
//                 {/* Hidden below sm so the pills still fit on a small phone;
//                     the icon carries it, and aria-label/title keep it readable
//                     to a screen reader and on hover. */}
//                 <span className="d-none d-sm-inline">{memberLink.label}</span>
//               </Link>
//             )}
//           </div>
//         </div>
//       </nav>
